import type { Area, Report, ReportType, Severity } from '../types'
import { icons } from './icons'
import { escapeHtml } from './highlight'

const REPORT_TYPES: Array<{ key: ReportType; label: string }> = [
  { key: 'bug', label: 'Bug' },
  { key: 'suggestion', label: 'Suggestion' },
]
const AREAS: Array<{ key: Area; label: string }> = [
  { key: 'ux', label: 'UX' },
  { key: 'visual', label: 'Visual' },
  { key: 'content', label: 'Content' },
  { key: 'performance', label: 'Performance' },
  { key: 'functional', label: 'Functional' },
]
const SEVERITIES: Severity[] = ['blocker', 'major', 'minor', 'trivial']
const DESC_MAX = 1000
const EXPECTED_MAX = 500

export interface DialogResult {
  description: string
  expected?: string
  report_type: ReportType
  area: Area
  severity?: Severity
  severity_touched: boolean
  /** Keys of captured-context items the reviewer chose to exclude. */
  excludedContext: string[]
  includeScreenshot: boolean
  screenshot: string | null
}

export interface DialogCallbacks {
  onSubmit(values: DialogResult): void
  onCancel(): void
  onCrumbClick(el: Element): void
  onDirtyChange(dirty: boolean): void
  /** Capture a screenshot for the inline thumbnail; null if unavailable. */
  onRequestScreenshot(): Promise<string | null>
}

/** A named captured-context item shown to the reviewer; non-core items are excludable. */
interface CtxItem {
  key: string
  name: string
  value: string
  core?: boolean
}

// Anchored popover — role=dialog aria-modal. Light monday.com styling. Positioned
// so it never covers the picked element nor renders under the drawer.
export class Dialog {
  insets: { left: number; right: number } = { left: 0, right: 0 }
  private root: ShadowRoot
  private el: HTMLElement | null = null
  private anchor: Element | null = null
  private chain: Element[] = []
  private cbs: DialogCallbacks
  private reportType: ReportType | null = null
  private area: Area | null = null
  private severity: Severity = 'minor'
  private severityTouched = false
  private excluded = new Set<string>()
  private includeScreenshot = true
  private screenshotUrl: string | null = null
  private wasDirty = false
  private confirmingDiscard = false
  private raf = 0
  private reposition = () => this.schedule()
  private titleId = 'qc-dlg-title'

  constructor(root: ShadowRoot, cbs: DialogCallbacks) {
    this.root = root
    this.cbs = cbs
  }

  get isOpen(): boolean {
    return !!this.el
  }

  open(anchor: Element): void {
    this.close()
    this.anchor = anchor
    this.reportType = null
    this.area = null
    this.severity = 'minor'
    this.severityTouched = false
    this.excluded = new Set()
    this.includeScreenshot = true
    this.screenshotUrl = null
    this.wasDirty = false
    this.confirmingDiscard = false

    const el = document.createElement('div')
    el.className = 'qc-dialog'
    el.setAttribute('role', 'dialog')
    el.setAttribute('aria-modal', 'true')
    el.setAttribute('aria-labelledby', this.titleId)
    el.innerHTML = `
      <div class="qc-dialog-head">
        <div class="qc-head-txt">
          <div class="qc-dialog-title" id="${this.titleId}">${icons.crosshair}<span>Report an issue</span></div>
          <div class="qc-crumb-wrap">
            <div class="qc-crumb-hint">Element path: ◂ ▸ or Alt+↑ ↓ to change level</div>
            <div class="qc-crumbs" role="group" aria-label="Element path" data-ref="crumbs"></div>
          </div>
          <div class="qc-idchip" data-ref="idchip"></div>
          <div class="qc-lostnote" data-ref="lost" hidden>Element no longer on page; captured context preserved</div>
        </div>
        <button class="qc-iconbtn" data-ref="close" aria-label="Close report dialog">${icons.x}</button>
      </div>
      <div class="qc-dialog-body">
        <div class="qc-field">
          <label for="qc-desc">What happened?<span class="qc-req" aria-hidden="true">*</span></label>
          <p class="qc-help" id="qc-desc-help">Example: the button stays disabled after I fill the form.</p>
          <textarea id="qc-desc" class="qc-input" data-ref="desc" rows="2" maxlength="${DESC_MAX}"
            aria-describedby="qc-desc-help qc-desc-count" required></textarea>
          <div class="qc-counter" id="qc-desc-count" data-ref="descCount">0 / ${DESC_MAX}</div>
        </div>
        <div class="qc-field">
          <label for="qc-expected">What did you expect?</label>
          <textarea id="qc-expected" class="qc-input" data-ref="expected" rows="1" maxlength="${EXPECTED_MAX}"
            aria-describedby="qc-exp-count"></textarea>
          <div class="qc-counter" id="qc-exp-count" data-ref="expCount">0 / ${EXPECTED_MAX}</div>
        </div>
        <fieldset class="qc-fieldset qc-field">
          <legend id="qc-type-label">Report type<span class="qc-req" aria-hidden="true">*</span></legend>
          <div class="qc-chips" role="radiogroup" aria-labelledby="qc-type-label" aria-required="true" data-ref="types"></div>
        </fieldset>
        <fieldset class="qc-fieldset qc-field">
          <legend id="qc-area-label">Area<span class="qc-req" aria-hidden="true">*</span></legend>
          <div class="qc-chips" role="radiogroup" aria-labelledby="qc-area-label" aria-required="true" data-ref="areas"></div>
        </fieldset>
        <fieldset class="qc-fieldset qc-field" data-ref="sevField">
          <legend id="qc-sev-label">Severity<span class="qc-req" aria-hidden="true">*</span></legend>
          <div class="qc-seg" role="radiogroup" aria-labelledby="qc-sev-label" aria-required="true" data-ref="sevs"></div>
        </fieldset>
        <div class="qc-ctxbox">
          <div class="qc-ctxhead">${icons.info}<span>Captured context</span><span class="qc-badge" data-ref="ctxcount"></span></div>
          <ul class="qc-ctxlist" data-ref="ctx"></ul>
          <div class="qc-shot" data-ref="shot" hidden></div>
        </div>
      </div>
      <div class="qc-dialog-foot" data-ref="foot">
        <span class="qc-foot-hint"><kbd>Ctrl</kbd>+<kbd>Enter</kbd> to submit</span>
        <button class="qc-btn qc-btn-ghost" data-ref="cancel">Cancel</button>
        <button class="qc-btn qc-btn-submit" data-ref="submit">${icons.send}<span>Submit report</span></button>
      </div>
    `
    this.el = el
    this.root.appendChild(el)

    this.buildRadioGroup('types', REPORT_TYPES, (k) => this.setReportType(k as ReportType))
    this.buildRadioGroup('areas', AREAS, (k) => this.setArea(k as Area))
    this.buildSeverity()

    const desc = this.ref<HTMLTextAreaElement>('desc')
    const expected = this.ref<HTMLTextAreaElement>('expected')
    desc.addEventListener('input', () => this.onInput())
    expected.addEventListener('input', () => this.onInput())

    this.ref('close').addEventListener('click', () => this.requestCancel())
    this.ref('cancel').addEventListener('click', () => this.requestCancel())
    this.ref('submit').addEventListener('click', () => this.trySubmit())

    el.addEventListener('keydown', (e) => this.onKeydown(e))

    this.updateAnchor(anchor)
    window.addEventListener('scroll', this.reposition, { capture: true, passive: true })
    window.addEventListener('resize', this.reposition, { passive: true })
    this.position()
    desc.focus()
  }

  private buildRadioGroup(
    ref: string,
    items: Array<{ key: string; label: string }>,
    onPick: (k: string) => void,
  ): void {
    const group = this.ref(ref)
    items.forEach((it, i) => {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'qc-chip'
      b.setAttribute('role', 'radio')
      b.setAttribute('aria-checked', 'false')
      b.tabIndex = i === 0 ? 0 : -1 // roving tabindex: group is a single tab stop
      b.dataset.key = it.key
      b.innerHTML = `<span class="qc-check">${icons.check}</span><span>${escapeHtml(it.label)}</span>`
      b.addEventListener('click', () => onPick(it.key))
      group.appendChild(b)
    })
    this.radioArrowNav(group, onPick)
  }

  private buildSeverity(): void {
    const sevs = this.ref('sevs')
    SEVERITIES.forEach((s, i) => {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = `qc-chip qc-sev-${s}`
      b.setAttribute('role', 'radio')
      b.setAttribute('aria-checked', 'false')
      b.tabIndex = i === 0 ? 0 : -1
      b.dataset.key = s
      b.innerHTML = `<span class="qc-check">${icons.check}</span><span class="qc-sevdot"></span><span>${s[0].toUpperCase() + s.slice(1)}</span>`
      b.addEventListener('click', () => this.setSeverity(s))
      sevs.appendChild(b)
    })
    this.radioArrowNav(this.ref('sevs'), (k) => this.setSeverity(k as Severity))
  }

  private onInput(): void {
    const desc = this.ref<HTMLTextAreaElement>('desc')
    const expected = this.ref<HTMLTextAreaElement>('expected')
    this.setCounter('descCount', desc.value.length, DESC_MAX)
    this.setCounter('expCount', expected.value.length, EXPECTED_MAX)
    const dirty = this.dirty
    if (dirty !== this.wasDirty) {
      this.wasDirty = dirty
      this.cbs.onDirtyChange(dirty)
    }
  }

  private setCounter(ref: string, n: number, max: number): void {
    const el = this.ref(ref)
    el.textContent = `${n} / ${max}`
    el.classList.toggle('qc-over', n >= max)
  }

  /** Refresh breadcrumb / idchip for a (re)targeted anchor. */
  updateAnchor(anchor: Element): void {
    if (!this.el) return
    this.anchor = anchor
    const crumbs = this.ref('crumbs')
    crumbs.innerHTML = ''
    const chain: Element[] = []
    let node: Element | null = anchor
    while (node && chain.length < 6) {
      chain.unshift(node)
      node = node.parentElement
    }
    this.chain = chain
    chain.forEach((c, i) => {
      if (i > 0) {
        const sep = document.createElement('span')
        sep.className = 'qc-crumb-sep'
        sep.setAttribute('aria-hidden', 'true')
        sep.textContent = '›'
        crumbs.appendChild(sep)
      }
      const b = document.createElement('button')
      b.type = 'button'
      b.className = `qc-crumb${c === anchor ? ' qc-current' : ''}`
      let label = c.tagName.toLowerCase()
      if (c.classList.length) label += `.${c.classList[0]}`
      b.textContent = label
      b.setAttribute('aria-label', `${label}, level ${i + 1} of ${chain.length}`)
      if (c === anchor) b.setAttribute('aria-current', 'true')
      b.addEventListener('click', () => this.cbs.onCrumbClick(c))
      b.addEventListener('keydown', (e) => this.onCrumbKey(e, i))
      crumbs.appendChild(b)
    })
    const rect = anchor.getBoundingClientRect()
    let idLabel = anchor.tagName.toLowerCase()
    if (anchor.id) idLabel = `#${anchor.id}`
    else if (anchor.classList.length) idLabel = `.${anchor.classList[0]}`
    this.ref('idchip').textContent = `${idLabel} · ${Math.round(rect.width)}×${Math.round(rect.height)} px`
    this.ref('lost').hidden = true
    this.position()
  }

  private onCrumbKey(e: KeyboardEvent, idx: number): void {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    e.stopPropagation()
    const next = e.key === 'ArrowLeft' ? idx - 1 : idx + 1
    const target = this.chain[next]
    if (target) this.cbs.onCrumbClick(target)
  }

  /** Screenshot: request capture, show inline thumbnail with retake/exclude. */
  async loadScreenshot(): Promise<void> {
    if (!this.el) return
    const shot = this.ref('shot')
    shot.hidden = false
    shot.innerHTML = `<div class="qc-help">Capturing screenshot…</div>`
    let url: string | null = null
    try {
      url = await this.cbs.onRequestScreenshot()
    } catch {
      url = null
    }
    if (!this.el) return
    this.screenshotUrl = url
    this.renderScreenshot()
  }

  private renderScreenshot(): void {
    const shot = this.ref('shot')
    if (!this.screenshotUrl) {
      shot.innerHTML = `<div class="qc-help">Screenshot unavailable on this page.</div>`
      return
    }
    if (!this.includeScreenshot) {
      shot.innerHTML = `<div class="qc-ctxitem" style="padding-left:0;border-top:0"><span class="qc-ctxname">Screenshot</span><span class="qc-ctxval">excluded</span><button class="qc-shot-inc qc-link" style="color:var(--qc-accent-strong);text-decoration:underline">Include</button></div>`
      shot.querySelector('.qc-shot-inc')!.addEventListener('click', () => {
        this.includeScreenshot = true
        this.renderScreenshot()
      })
      return
    }
    const img = new Image()
    img.alt = 'Screenshot of the reported area'
    img.onload = () => this.schedule() // reposition once the thumbnail's real height is known
    img.src = this.screenshotUrl
    shot.innerHTML = `<div class="qc-shot-actions"><button data-ref="retake">Retake</button><button data-ref="exclude">Exclude screenshot</button></div>`
    shot.prepend(img)
    shot.querySelector('[data-ref="retake"]')!.addEventListener('click', () => void this.loadScreenshot())
    shot.querySelector('[data-ref="exclude"]')!.addEventListener('click', () => {
      this.includeScreenshot = false
      this.renderScreenshot()
    })
    this.schedule()
  }

  /** Render the always-visible captured-context list from a pre-send report. */
  showContext(report: Report): void {
    if (!this.el) return
    const t = report.target
    const env = report.environment
    const h = report.source_hints
    const isRegion = t.kind === 'region'
    const items: CtxItem[] = [
      { key: 'element', name: 'Element', value: `${t.css_selector} · ${t.dom_path}`, core: true },
      { key: 'url', name: 'Page URL', value: t.url, core: true },
    ]
    if (t.route) items.push({ key: 'route', name: 'Route', value: t.route, core: true })
    if (isRegion) items.push({ key: 'region', name: 'Region', value: `${t.region_box?.w}×${t.region_box?.h} · ${t.region_elements?.length ?? 0} elements`, core: true })
    if (env?.viewport) items.push({ key: 'viewport', name: 'Viewport', value: `${env.viewport.w}×${env.viewport.h} @${env.viewport.dpr}x` })
    if (env?.user_agent) items.push({ key: 'user_agent', name: 'User agent', value: env.user_agent })
    if (env?.console_errors) items.push({ key: 'console', name: 'Console errors', value: `${env.console_errors.length} captured` })
    if (t.outer_html_excerpt) items.push({ key: 'dom_snippet', name: 'DOM snippet', value: `${t.outer_html_excerpt.length} chars` })
    if (t.matched_rules?.length || t.computed_styles) {
      const sel = (t.matched_rules ?? []).filter((r) => r.source !== 'style-attr').slice(0, 2).map((r) => r.selector).join(', ')
      items.push({ key: 'css', name: 'CSS styles', value: `${t.matched_rules?.length ?? 0} rules${sel ? ' · ' + sel : ''}` })
    }
    if (h?.component_stack?.length || h?.source_location) {
      items.push({ key: 'component', name: 'Component / source', value: [h.component_stack?.join(' › '), h.source_location].filter(Boolean).join(', ') })
    }

    const list = this.ref('ctx')
    list.innerHTML = ''
    for (const it of items) {
      const li = document.createElement('li')
      li.className = 'qc-ctxitem'
      const included = !this.excluded.has(it.key)
      li.innerHTML =
        `<span class="qc-ctxname">${escapeHtml(it.name)}</span>` +
        `<span class="qc-ctxval" title="${escapeHtml(it.value)}">${escapeHtml(it.value.slice(0, 80))}</span>`
      if (!it.core) {
        const sw = document.createElement('button')
        sw.type = 'button'
        sw.className = 'qc-switch qc-ctxtoggle'
        sw.setAttribute('role', 'switch')
        sw.setAttribute('aria-checked', String(included))
        sw.setAttribute('aria-label', `Include ${it.name}`)
        sw.addEventListener('click', () => {
          const on = sw.getAttribute('aria-checked') !== 'true'
          sw.setAttribute('aria-checked', String(on))
          if (on) this.excluded.delete(it.key)
          else this.excluded.add(it.key)
        })
        li.appendChild(sw)
      } else {
        const tag = document.createElement('span')
        tag.className = 'qc-badge qc-ctxtoggle'
        tag.textContent = 'always'
        li.appendChild(tag)
      }
      list.appendChild(li)
    }
    this.ref('ctxcount').textContent = `${items.length} items`
    this.schedule() // content height changed — re-clamp position so the foot stays in view
  }

  /** Restore a prior draft's fields (used when a submit is undone). */
  restore(v: DialogResult): void {
    if (!this.el) return
    this.ref<HTMLTextAreaElement>('desc').value = v.description
    this.ref<HTMLTextAreaElement>('expected').value = v.expected ?? ''
    this.setReportType(v.report_type)
    this.setArea(v.area)
    if (v.report_type === 'bug' && v.severity && v.severity_touched) this.setSeverity(v.severity)
    this.excluded = new Set(v.excludedContext)
    this.includeScreenshot = v.includeScreenshot
    this.screenshotUrl = v.screenshot
    this.renderScreenshot()
    this.onInput()
  }

  markElementLost(): void {
    if (this.el) this.ref('lost').hidden = false
  }

  get dirty(): boolean {
    if (!this.el) return false
    return (this.ref<HTMLTextAreaElement>('desc').value.trim() + this.ref<HTMLTextAreaElement>('expected').value.trim()).length > 0
  }

  /** Esc handler routed from the global key listener. Returns 'blurred' | 'closing' | 'none'. */
  handleEsc(): 'blurred' | 'closing' | 'none' {
    if (!this.el) return 'none'
    const active = this.root.activeElement
    if (active && active.tagName === 'TEXTAREA') {
      ;(active as HTMLElement).blur()
      this.ref<HTMLElement>('submit').focus()
      return 'blurred'
    }
    this.requestCancel()
    return 'closing'
  }

  /** Cancel/close entry — confirms discard when dirty. */
  requestCancel(): boolean {
    if (!this.el) return false
    if (this.dirty && !this.confirmingDiscard) {
      this.setDiscardConfirm(true)
      return true
    }
    this.cbs.onCancel()
    return true
  }

  values(): DialogResult | null {
    if (!this.el) return null
    const desc = this.ref<HTMLTextAreaElement>('desc').value.trim()
    if (!desc || !this.reportType || !this.area) return null
    const expected = this.ref<HTMLTextAreaElement>('expected').value.trim()
    const isBug = this.reportType === 'bug'
    return {
      description: desc,
      ...(expected ? { expected } : {}),
      report_type: this.reportType,
      area: this.area,
      ...(isBug ? { severity: this.severity } : {}),
      severity_touched: isBug ? this.severityTouched : false,
      excludedContext: [...this.excluded],
      includeScreenshot: this.includeScreenshot,
      screenshot: this.includeScreenshot ? this.screenshotUrl : null,
    }
  }

  close(): void {
    window.removeEventListener('scroll', this.reposition, { capture: true } as EventListenerOptions)
    window.removeEventListener('resize', this.reposition)
    this.el?.remove()
    this.el = null
    this.anchor = null
    if (this.wasDirty) {
      this.wasDirty = false
      this.cbs.onDirtyChange(false)
    }
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      this.trySubmit()
    } else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      // Breadcrumb traversal (parent/child) — Alt+↑/↓, never Esc.
      e.preventDefault()
      e.stopPropagation()
      const cur = this.chain.indexOf(this.anchor!)
      const next = e.key === 'ArrowUp' ? cur - 1 : cur + 1
      if (this.chain[next]) this.cbs.onCrumbClick(this.chain[next])
    } else if (e.key === 'Tab') {
      this.trapFocus(e)
    }
  }

  private trySubmit(): void {
    const vals = this.values()
    if (!vals) {
      const desc = this.ref<HTMLTextAreaElement>('desc')
      if (!desc.value.trim()) {
        desc.setAttribute('aria-invalid', 'true')
        desc.focus()
      } else if (!this.reportType) {
        ;(this.ref('types').querySelector('button') as HTMLButtonElement).focus()
      } else {
        ;(this.ref('areas').querySelector('button') as HTMLButtonElement).focus()
      }
      return
    }
    this.ref('desc').removeAttribute('aria-invalid')
    this.cbs.onSubmit(vals)
  }

  private setReportType(t: ReportType): void {
    this.reportType = t
    this.markChecked('types', t)
    // Hide Severity entirely for Suggestions.
    this.ref('sevField').hidden = t === 'suggestion'
    this.position()
  }

  private setArea(a: Area): void {
    this.area = a
    this.markChecked('areas', a)
  }

  private setSeverity(sev: Severity): void {
    this.severity = sev
    this.severityTouched = true
    this.markChecked('sevs', sev)
  }

  private markChecked(ref: string, key: string): void {
    this.ref(ref)
      .querySelectorAll('[role="radio"]')
      .forEach((b) => {
        const on = (b as HTMLElement).dataset.key === key
        b.setAttribute('aria-checked', String(on))
        ;(b as HTMLElement).tabIndex = on ? 0 : -1 // roving tabindex follows selection
      })
  }

  private setDiscardConfirm(on: boolean): void {
    this.confirmingDiscard = on
    const foot = this.ref('foot')
    if (on) {
      foot.innerHTML = `
        <div class="qc-confirm-discard">
          <span class="qc-spacer">Discard this draft? Your text will be lost.</span>
          <button class="qc-discard-yes" data-ref="discardYes">Discard</button>
          <button data-ref="discardNo">Keep writing</button>
        </div>`
      foot.querySelector('[data-ref="discardYes"]')!.addEventListener('click', () => this.cbs.onCancel())
      const no = foot.querySelector('[data-ref="discardNo"]') as HTMLButtonElement
      no.addEventListener('click', () => {
        this.setDiscardConfirm(false)
        this.ref<HTMLTextAreaElement>('desc').focus()
      })
      no.focus()
    } else {
      foot.innerHTML = `
        <span class="qc-foot-hint"><kbd>Ctrl</kbd>+<kbd>Enter</kbd> to submit</span>
        <button class="qc-btn qc-btn-ghost" data-ref="cancel">Cancel</button>
        <button class="qc-btn qc-btn-submit" data-ref="submit">${icons.send}<span>Submit report</span></button>`
      foot.querySelector('[data-ref="cancel"]')!.addEventListener('click', () => this.requestCancel())
      foot.querySelector('[data-ref="submit"]')!.addEventListener('click', () => this.trySubmit())
    }
  }

  private radioArrowNav(group: HTMLElement, onPick: (k: string) => void): void {
    group.addEventListener('keydown', (e) => {
      if (e.altKey) return // Alt+arrows are breadcrumb traversal
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return
      const radios = Array.from(group.querySelectorAll<HTMLButtonElement>('[role="radio"]'))
      const idx = radios.indexOf(this.root.activeElement as HTMLButtonElement)
      if (idx === -1) return
      e.preventDefault()
      e.stopPropagation()
      const dir = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1
      const next = radios[(idx + dir + radios.length) % radios.length]
      radios.forEach((r) => (r.tabIndex = -1))
      next.tabIndex = 0
      next.focus()
      onPick(next.dataset.key!)
    })
  }

  private trapFocus(e: KeyboardEvent): void {
    if (!this.el) return
    const focusables = Array.from(
      this.el.querySelectorAll<HTMLElement>('button:not([hidden]), textarea, [tabindex]:not([tabindex="-1"]), [role="switch"], [role="radio"]'),
    ).filter((f) => !f.hasAttribute('disabled') && f.offsetParent !== null)
    if (!focusables.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = this.root.activeElement
    if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  private ref<T extends HTMLElement = HTMLElement>(name: string): T {
    return this.el!.querySelector(`[data-ref="${name}"]`) as T
  }

  private schedule(): void {
    if (this.raf || !this.el) return
    this.raf = requestAnimationFrame(() => {
      this.raf = 0
      this.position()
    })
  }

  private position(): void {
    if (!this.el || !this.anchor || !this.anchor.isConnected) return
    // Full-width bottom sheet under 640px effective width or when reflowed (400% zoom).
    if (window.innerWidth < 640) {
      this.el.classList.add('qc-sheet')
      this.el.style.left = ''
      this.el.style.top = ''
      return
    }
    this.el.classList.remove('qc-sheet')
    const r = this.anchor.getBoundingClientRect()
    const dw = this.el.offsetWidth || 400
    const dh = this.el.offsetHeight || 360
    const minX = 8 + this.insets.left
    const maxX = window.innerWidth - this.insets.right - 8
    const vh = window.innerHeight
    const gap = 12
    let left: number
    let top: number
    // Flip/shift so the popover never covers the element nor hides under the drawer.
    if (r.right + gap + dw <= maxX) {
      left = r.right + gap
      top = r.top
    } else if (r.left - gap - dw >= minX) {
      left = r.left - gap - dw
      top = r.top
    } else if (r.bottom + gap + dh <= vh) {
      left = Math.min(Math.max(minX, r.left), maxX - dw)
      top = r.bottom + gap
    } else {
      left = Math.min(Math.max(minX, r.left), maxX - dw)
      top = Math.max(8, r.top - gap - dh)
    }
    top = Math.min(Math.max(8, top), Math.max(8, vh - dh - 8))
    Object.assign(this.el.style, { left: `${left}px`, top: `${top}px` })
  }
}
